import type { CSSProperties } from 'react';

// Per-finding classification summary, present iff a council run was a
// verifier-mode pass.
export interface VerifierResultsCounts {
  resolved: number;
  unresolved: number;
  partial: number;
  withdrawn: number;
}

// Defensive coercion: a partial verifier_results object (e.g., a runner
// regression that drops one of the four count keys) still renders the chip,
// with missing keys clamped to 0. Non-object input drops the chip entirely.
export function parseVerifierResults(raw: unknown): VerifierResultsCounts | null {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const num = (k: string): number => {
    const v = o[k];
    return typeof v === 'number' && Number.isFinite(v) && v >= 0 ? v : 0;
  };
  return {
    resolved: num('resolved'),
    unresolved: num('unresolved'),
    partial: num('partial'),
    withdrawn: num('withdrawn'),
  };
}

// Shared small-pill style. Visual weight is deliberately low — these are
// sub-context next to a primary label, not standalone status.
const BADGE_STYLE_BASE: CSSProperties = {
  display: 'inline-block',
  marginLeft: 6,
  padding: '1px 6px',
  borderRadius: 999,
  fontSize: '0.625rem',
  fontWeight: 528,
  verticalAlign: 'middle',
};

export function ReReviewBadge() {
  return (
    <span
      style={{
        ...BADGE_STYLE_BASE,
        background: 'var(--loop-neutral-200)',
        color: 'var(--loop-neutral-700)',
      }}
      title="This council run re-reviewed an existing PR rather than reviewing it for the first time."
    >
      RE-REVIEW
    </span>
  );
}

export function AutoBadge() {
  return (
    <span
      style={{
        ...BADGE_STYLE_BASE,
        background: 'var(--loop-blue-100)',
        color: 'var(--loop-blue-700)',
      }}
      title="This council run was auto-triggered (push-on-PR), not user-initiated via slash command."
    >
      AUTO
    </span>
  );
}

// Compact 4-icon breakdown for verifier_results. Each count+icon span owns
// its own title attribute so a reader hovering over a single icon (rather
// than the whole chip) gets the legend for THAT icon. Previously the chip
// had one title with the full legend, which made decoding a single ◐ or ↩
// require reading the whole sentence. See #140.
export function VerifierBreakdown({ counts }: { counts: VerifierResultsCounts }) {
  return (
    <span
      data-testid="verifier-breakdown"
      style={{ fontFamily: 'monospace', fontSize: '0.75rem', color: 'var(--loop-neutral-900)' }}
    >
      <span style={{ color: 'var(--loop-green-700)' }} title="resolved">{counts.resolved} ✅</span>
      <span style={{ color: 'var(--loop-neutral-500)' }}> / </span>
      <span style={{ color: 'var(--loop-yellow-800)' }} title="unresolved">{counts.unresolved} ⚠️</span>
      <span style={{ color: 'var(--loop-neutral-500)' }}> / </span>
      <span style={{ color: 'var(--loop-neutral-700)' }} title="partial">{counts.partial} ◐</span>
      <span style={{ color: 'var(--loop-neutral-500)' }}> / </span>
      <span style={{ color: 'var(--loop-neutral-700)' }} title="withdrawn">{counts.withdrawn} ↩</span>
    </span>
  );
}
